// Dashboard (index.html) specific JavaScript

import {
    apiRequest,
    initAuthenticatedPage,
    showToast,
    GitHubSyncResult,
    getDefaultProjectId,
} from "./common";

interface DashboardStats {
    ticket_count: number;
    project_count: number;
    docs_count: number;
}

interface Project {
    id: number;
    name: string;
    key: string;
    github_enabled: boolean;
    github_repo_owner: string | null;
    github_repo_name: string | null;
}

async function loadDashboardStats(): Promise<void> {
    const ticketCount = document.getElementById("ticket-count");
    const projectCount = document.getElementById("project-count");
    const docsCount = document.getElementById("docs-count");

    if (!(ticketCount instanceof HTMLElement)) {
        console.error("ticket-count element not found");
        return;
    }
    if (!(projectCount instanceof HTMLElement)) {
        console.error("project-count element not found");
        return;
    }
    if (!(docsCount instanceof HTMLElement)) {
        console.error("docs-count element not found");
        return;
    }

    try {
        const stats = await apiRequest<DashboardStats>("/dashboard/stats");
        ticketCount.textContent = stats.ticket_count.toString();
        projectCount.textContent = stats.project_count.toString();
        docsCount.textContent = stats.docs_count.toString();
    } catch (error) {
        console.error("Failed to load dashboard stats:", error);
        ticketCount.textContent = "Error";
        projectCount.textContent = "Error";
        docsCount.textContent = "Error";
    }
}

async function loadProjects(): Promise<void> {
    try {
        const projects = await apiRequest<Project[]>("/projects");

        // Setup GitHub sync section
        setupGitHubSync(projects);
    } catch (error) {
        console.error("Failed to load projects:", error);
    }
}

function setupGitHubSync(projects: Project[]): void {
    const githubSyncSection = document.getElementById("github-sync-section");
    const defaultProjectName = document.getElementById("default-project-name");
    const syncGitHubBtn = document.getElementById(
        "sync-github-btn"
    ) as HTMLButtonElement;

    if (!githubSyncSection || !defaultProjectName || !syncGitHubBtn) {
        return;
    }

    // Get the default project
    const defaultProjectId = getDefaultProjectId();
    if (!defaultProjectId) {
        githubSyncSection.style.display = "none";
        return;
    }

    // Find the default project and check if it has GitHub enabled
    const defaultProject = projects.find(
        (p) => p.id === defaultProjectId && p.github_enabled
    );

    if (!defaultProject) {
        githubSyncSection.style.display = "none";
        return;
    }

    // Show section with project name
    githubSyncSection.style.display = "block";
    defaultProjectName.textContent = defaultProject.name;

    // Handle sync button click
    syncGitHubBtn.addEventListener("click", async () => {
        syncGitHubBtn.setAttribute("aria-busy", "true");
        syncGitHubBtn.disabled = true;

        showToast(`Syncing ${defaultProject.name} with GitHub...`, "info");

        try {
            const result = await apiRequest<GitHubSyncResult>(
                `/github/projects/${defaultProject.id}/sync`,
                {
                    method: "POST",
                }
            );

            showToast(
                `Sync complete! ${result.branches_synced} branches, ${result.pull_requests_synced} PRs.`,
                "success",
                7000
            );
        } catch (error) {
            showToast(
                `Sync failed: ${error instanceof Error ? error.message : "Unknown error"}`,
                "error",
                7000
            );
        } finally {
            syncGitHubBtn.removeAttribute("aria-busy");
            syncGitHubBtn.disabled = false;
        }
    });
}

async function initDashboard(): Promise<void> {
    // Wait for authentication to complete before making API requests
    // This prevents race conditions when the access token needs to be refreshed
    const user = await initAuthenticatedPage();
    if (!user) {
        // Auth failed, user is being redirected to login
        return;
    }

    // Now that auth is confirmed, load dashboard data
    loadDashboardStats();
    loadProjects();
}

initDashboard();
